import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { GbeErrorCodes } from '@core/models/gbe/gbe-error-codes.enum';
import { GbeError } from '@core/models/gbe/gbe-error.model';
import {
  LOG_SEVERITIES,
  LogSeverity,
  OptimizationLogEntry,
  severityVariant
} from '@core/models/optimization-logs.model';
import { ICONS } from '@core/services/icons/icons.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { OptimizationLogsService } from '@core/services/optimization-logs/optimization-logs.service';
import { downloadBlob } from '@core/utils/download.utils';
import { foldForSearch } from '@core/utils/search-text.utils';
import { isNil } from '@core/utils/shared-utils';

import { ButtonComponent } from '@shared/components/button/button.component';

/** Stop auto-following empty Cloud Logging pages after this many chained hops. */
const MAX_EMPTY_CHAIN = 5;
/** Switch to windowed rendering once the filtered list exceeds this many rows. */
const VIRTUALIZE_THRESHOLD = 200;
/** Fixed row height (px) used by the virtual scroll viewport. */
const ROW_HEIGHT = 28;

type LogsErrorKind = 'retention' | 'notFound' | 'noAccess' | 'generic';

interface LogsErrorState {
  kind: LogsErrorKind;
  canRetry: boolean;
  explanation?: string;
}

@Component({
  selector: 'app-optimization-job-logs',
  imports: [NgTemplateOutlet, ScrollingModule, TranslatePipe, ButtonComponent],
  templateUrl: './optimization-job-logs.component.html',
  styleUrl: './optimization-job-logs.component.css'
})
export class OptimizationJobLogsComponent {
  readonly optimizationId = input.required<string>();
  readonly pageSize = input(200);

  private readonly logsService = inject(OptimizationLogsService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly ICONS = ICONS;
  protected readonly severities = LOG_SEVERITIES;
  protected readonly rowHeight = ROW_HEIGHT;

  protected readonly entries = signal<OptimizationLogEntry[]>([]);
  protected readonly nextPageToken = signal<string | undefined>(undefined);
  protected readonly initialLoading = signal(false);
  protected readonly loadingMore = signal(false);
  protected readonly chaining = signal(false);
  protected readonly errorState = signal<LogsErrorState | null>(null);

  protected readonly searchTerm = signal('');
  /** Hidden severities (upper-cased). Empty = all shown. */
  protected readonly hiddenSeverities = signal<ReadonlySet<string>>(new Set());
  protected readonly wrap = signal(true);

  private inFlight?: Subscription;
  private generation = 0;
  private readonly seenKeys = new Set<string>();

  protected readonly filteredEntries = computed(() => {
    const term = foldForSearch(this.searchTerm().trim());
    const hidden = this.hiddenSeverities();
    return this.entries().filter((e) => {
      if (hidden.has((e.severity ?? '').toUpperCase())) {
        return false;
      }
      if (!term) {
        return true;
      }
      return (
        foldForSearch(e.message).includes(term) || foldForSearch(e.severity).includes(term)
      );
    });
  });

  protected readonly matchCount = computed(() => this.filteredEntries().length);
  protected readonly totalCount = computed(() => this.entries().length);
  protected readonly hasMore = computed(
    () => !isNil(this.nextPageToken()) && !this.errorState()
  );
  protected readonly virtualize = computed(
    () => this.filteredEntries().length > VIRTUALIZE_THRESHOLD
  );
  protected readonly isEmpty = computed(
    () =>
      !this.initialLoading() &&
      !this.chaining() &&
      !this.errorState() &&
      this.entries().length === 0
  );

  constructor() {
    effect(() => {
      const id = this.optimizationId();
      this.startInitialLoad(id);
    });
  }

  protected loadMore(): void {
    const token = this.nextPageToken();
    if (isNil(token) || this.loadingMore() || this.initialLoading()) {
      return;
    }
    this.loadingMore.set(true);
    this.fetchPage(this.optimizationId(), token, 'more', 0, this.generation);
  }

  protected retry(): void {
    this.startInitialLoad(this.optimizationId());
  }

  protected refresh(): void {
    this.startInitialLoad(this.optimizationId());
  }

  protected toggleSeverity(severity: LogSeverity): void {
    this.hiddenSeverities.update((current) => {
      const next = new Set(current);
      if (next.has(severity)) {
        next.delete(severity);
      } else {
        next.add(severity);
      }
      return next;
    });
  }

  protected isSeverityVisible(severity: LogSeverity): boolean {
    return !this.hiddenSeverities().has(severity);
  }

  protected toggleWrap(): void {
    this.wrap.update((w) => !w);
  }

  protected onSearchInput(value: string): void {
    this.searchTerm.set(value);
  }

  protected async copy(): Promise<void> {
    const text = this.entriesToText();
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      this.notification.success(
        this.translate.instant('features.plan.jobs.logs.copy'),
        this.translate.instant('features.plan.jobs.logs.copied', {
          count: this.filteredEntries().length
        })
      );
    } catch {
      this.notification.error(
        this.translate.instant('features.plan.jobs.logs.copy'),
        this.translate.instant('features.plan.jobs.logs.copyFailed')
      );
    }
  }

  protected download(): void {
    const text = this.entriesToText();
    if (!text) {
      return;
    }
    downloadBlob(
      new Blob([text], { type: 'text/plain;charset=utf-8' }),
      `job-${this.optimizationId()}-logs.txt`
    );
  }

  protected formatTime(iso: string | null): string {
    if (isNil(iso)) {
      return '—';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    const pad = (n: number, length = 2): string => String(n).padStart(length, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}.${pad(date.getMilliseconds(), 3)}`;
  }

  protected severityBadgeClass(severity: string): string {
    switch (severityVariant(severity)) {
      case 'danger':
        return 'bg-red-500/10 text-red-600 dark:bg-red-500/15 dark:text-red-400';
      case 'warning':
        return 'bg-amber-500/10 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400';
      case 'info':
        return 'bg-blue-500/10 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400';
      case 'muted':
        return 'bg-zinc-500/10 text-zinc-500 dark:bg-zinc-500/15 dark:text-zinc-400';
      default:
        return 'bg-zinc-500/10 text-text-secondary';
    }
  }

  protected severityBorderClass(severity: string): string {
    switch (severityVariant(severity)) {
      case 'danger':
        return 'border-l-red-500';
      case 'warning':
        return 'border-l-amber-500';
      case 'info':
        return 'border-l-blue-500';
      case 'muted':
        return 'border-l-zinc-400';
      default:
        return 'border-l-transparent';
    }
  }

  private startInitialLoad(id: string): void {
    this.generation += 1;
    this.inFlight?.unsubscribe();
    this.seenKeys.clear();
    this.entries.set([]);
    this.nextPageToken.set(undefined);
    this.errorState.set(null);
    this.loadingMore.set(false);
    this.chaining.set(false);
    this.initialLoading.set(true);
    this.fetchPage(id, undefined, 'initial', 0, this.generation);
  }

  private fetchPage(
    id: string,
    token: string | undefined,
    mode: 'initial' | 'more',
    emptyChain: number,
    generation: number
  ): void {
    this.inFlight?.unsubscribe();
    this.inFlight = this.logsService
      .fetchLogs$(id, { pageSize: this.pageSize(), pageToken: token })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (generation !== this.generation) {
            return;
          }
          const pageEntries = res.entries ?? [];
          const fresh = mode === 'initial' && emptyChain === 0;
          const newEntries = this.dedupe(pageEntries);
          this.entries.update((current) =>
            fresh ? newEntries : [...current, ...newEntries]
          );
          this.nextPageToken.set(res.nextPageToken);

          if (
            pageEntries.length === 0 &&
            !isNil(res.nextPageToken) &&
            emptyChain < MAX_EMPTY_CHAIN
          ) {
            this.chaining.set(true);
            this.fetchPage(id, res.nextPageToken, mode, emptyChain + 1, generation);
            return;
          }

          this.chaining.set(false);
          this.initialLoading.set(false);
          this.loadingMore.set(false);
        },
        error: (err) => {
          if (generation !== this.generation) {
            return;
          }
          this.chaining.set(false);
          this.initialLoading.set(false);
          this.loadingMore.set(false);
          this.errorState.set(this.mapError(err));
        }
      });
  }

  private mapError(err: unknown): LogsErrorState {
    const gbe = err as GbeError | undefined;
    const details = (gbe?.details ?? {}) as { explanation?: string };
    switch (gbe?.errorCode) {
      case GbeErrorCodes.LOGS_NO_LONGER_RETAINED:
        return { kind: 'retention', canRetry: false };
      case GbeErrorCodes.DATA_IS_FORBIDDEN_OR_NOT_FOUND_CODE:
        return { kind: 'notFound', canRetry: false, explanation: details.explanation };
      case GbeErrorCodes.FORBIDDEN_OPERATION_CODE:
      case GbeErrorCodes.FORBIDDEN_ENDPOINT_CODE:
        return { kind: 'noAccess', canRetry: false };
      default:
        return { kind: 'generic', canRetry: true };
    }
  }

  private entriesToText(): string {
    return this.filteredEntries()
      .map((e) => `${e.timestamp ?? '—'} ${e.severity} ${e.message}`)
      .join('\n');
  }

  private entryKey(e: OptimizationLogEntry): string {
    return `${e.timestamp ?? ''}|${e.severity}|${e.message}`;
  }

  private dedupe(incoming: OptimizationLogEntry[]): OptimizationLogEntry[] {
    const out: OptimizationLogEntry[] = [];
    for (const e of incoming) {
      const key = this.entryKey(e);
      if (this.seenKeys.has(key)) {
        continue;
      }
      this.seenKeys.add(key);
      out.push(e);
    }
    return out;
  }
}
